import { useMemo, useState } from 'react';
import TimeSheetHistoryEntry from 'components/timeSheetHistory/TimeSheetHistoryEntry';
import { isSystemEvent } from 'helper/timeSheetEventLabels';

/** One operation emits one event per sheet. Showing them all would bury the
 *  rest of the timeline under a single admin gesture. */
const collapseCorrelated = (events) => {
  const seen = new Map();
  const result = [];
  events.forEach((event) => {
    if (!event.correlationId) {
      result.push({ event, siblings: 1 });
      return;
    }
    const existing = seen.get(event.correlationId);
    if (existing) {
      existing.siblings += 1;
      return;
    }
    const entry = { event, siblings: 1 };
    seen.set(event.correlationId, entry);
    result.push(entry);
  });
  return result;
};

const MERGE_WINDOW_MS = 2 * 60 * 1000;

/** Two events the same person produced in one gesture: same action, same place,
 *  moments apart, on two different days of the sheet. */
const sameGesture = (a, b) =>
  Boolean(a && b) &&
  a.action === b.action &&
  a.source === b.source &&
  a.actor?.type === b.actor?.type &&
  a.actor?.displayName === b.actor?.displayName &&
  Boolean(a.dayRef) &&
  Boolean(b.dayRef) &&
  a.dayRef._id !== b.dayRef._id &&
  Math.abs(new Date(a.createdAt) - new Date(b.createdAt)) <= MERGE_WINDOW_MS;

/** Filling a week writes one event per day. Left apart they read as five
 *  separate actions, pushing everything else out of sight; together they read
 *  as what the person actually did once. */
const mergeSameGesture = (entries) => {
  const result = [];
  entries.forEach((entry) => {
    const previous = result[result.length - 1];
    const mergeable =
      previous &&
      previous.siblings === 1 &&
      entry.siblings === 1 &&
      previous.events.every((event) => sameGesture(event, entry.event));
    if (mergeable) {
      previous.events.push(entry.event);
      return;
    }
    result.push({
      key: entry.event?._id,
      events: [entry.event],
      siblings: entry.siblings,
    });
  });
  return result;
};

const TimeSheetHistoryList = ({ events = [], isLoading, error }) => {
  const [showSystem, setShowSystem] = useState(false);

  const entries = useMemo(() => {
    const visible = showSystem ? events : events.filter((e) => !isSystemEvent(e));
    return mergeSameGesture(collapseCorrelated(visible));
  }, [events, showSystem]);

  const systemCount = useMemo(
    () => events.filter(isSystemEvent).length,
    [events]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 bg-gray-100 rounded" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-sm text-gray-500">
        L’historique n’a pas pu être chargé. Rouvrez le panneau pour réessayer.
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="ft-sm text-gray-500">
        Aucun historique détaillé pour ce relevé : les modifications antérieures
        n’ont pas été conservées.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {systemCount > 0 ? (
        <label className="flex items-center gap-2 ft-sm text-gray-500 cursor-pointer pb-2">
          <input
            type="checkbox" className="checkbox-primary"
            checked={showSystem}
            onChange={(e) => setShowSystem(e.target.checked)}
          />
          Afficher les recalculs automatiques ({systemCount})
        </label>
      ) : null}

      <div className="flex flex-col divide-y divide-gray-50">
        {entries.map(({ key, events: gesture, siblings }) => (
          <div key={key}>
            <TimeSheetHistoryEntry events={gesture} />
            {siblings > 1 ? (
              <div className="ft-sm text-gray-400 pl-14 pb-3">
                {siblings} relevés touchés par cette opération
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimeSheetHistoryList;
